import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { ArrowLeft, Star, ThumbsUp, ThumbsDown, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminReviewsPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!token || user?.email !== 'test@velvet.ai') {
      navigate('/');
      return;
    }
    const fetchReviews = async () => {
      try {
        const res = await axios.get(`${API}/admin/reviews`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setReviews(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, [token, user, navigate]);

  const filtered = filter === 'all' ? reviews 
    : filter === 'positive' ? reviews.filter(r => r.liked) 
    : reviews.filter(r => !r.liked);

  const avgRating = reviews.filter(r => r.rating).reduce((sum, r) => sum + r.rating, 0) / (reviews.filter(r => r.rating).length || 1);

  return (
    <div className="min-h-screen bg-velvet-bg px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 text-velvet_text-secondary hover:text-velvet_text-accent mb-6 transition-colors">
          <ArrowLeft size={20} /> Retour
        </button>

        <h1 className="font-heading text-3xl text-velvet_text-primary mb-2" data-testid="admin-reviews-title">Avis utilisateurs</h1>
        
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-purple-light" size={32} /></div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="glass rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-velvet_text-primary">{reviews.length}</p>
                <p className="text-velvet_text-secondary text-xs">Total</p>
              </div>
              <div className="glass rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-green-400">{reviews.filter(r => r.liked).length}</p>
                <p className="text-velvet_text-secondary text-xs">Positifs</p>
              </div>
              <div className="glass rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-red-400">{reviews.filter(r => !r.liked).length}</p>
                <p className="text-velvet_text-secondary text-xs">Négatifs</p>
              </div>
            </div>

            {reviews.filter(r => r.rating).length > 0 && (
              <div className="glass rounded-xl p-4 mb-6 flex items-center gap-3">
                <span className="text-velvet_text-secondary text-sm">Note moyenne :</span>
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map(s => (
                    <Star key={s} size={18} className={s <= Math.round(avgRating) ? 'text-yellow-400 fill-yellow-400' : 'text-white/20'} />
                  ))}
                </div>
                <span className="text-velvet_text-primary font-bold">{avgRating.toFixed(1)}/5</span>
              </div>
            )}

            {/* Filters */}
            <div className="flex gap-2 mb-6">
              {[
                { key: 'all', label: 'Tous' },
                { key: 'positive', label: 'Positifs' },
                { key: 'negative', label: 'Négatifs' }
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    filter === f.key ? 'bg-purple-main text-white' : 'bg-white/10 text-velvet_text-secondary hover:bg-white/20'
                  }`}
                  data-testid={`filter-${f.key}`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Reviews list */}
            <div className="space-y-3">
              {filtered.length === 0 ? (
                <p className="text-velvet_text-secondary text-center py-10">Aucun avis</p>
              ) : (
                filtered.map((review) => (
                  <div key={review.id} className="glass rounded-xl p-4" data-testid={`review-${review.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {review.liked ? (
                          <ThumbsUp size={16} className="text-green-400" />
                        ) : (
                          <ThumbsDown size={16} className="text-red-400" />
                        )}
                        <span className="text-velvet_text-primary text-sm font-medium">
                          {review.user_email || 'Invité'}
                        </span>
                        {review.character_id && (
                          <span className="text-xs text-purple-light bg-purple-main/20 px-2 py-0.5 rounded-full">
                            {review.character_id}
                          </span>
                        )}
                      </div>
                      <span className="text-velvet_text-secondary text-xs">
                        {new Date(review.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    {review.rating && (
                      <div className="flex gap-0.5 mb-2">
                        {[1,2,3,4,5].map(s => (
                          <Star key={s} size={14} className={s <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-white/20'} />
                        ))}
                      </div>
                    )}
                    {review.comment && (
                      <p className="text-velvet_text-secondary text-sm">{review.comment}</p>
                    )}
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
